
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Upload, X, FileText } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { budgetStructure, expenseStatusOptions } from './budgetConfig';
import { UploadFile } from '@/integrations/Core';

const documentTypes = [
    { value: 'facture', label: 'Facture' },
    { value: 'bon_commande', label: 'Bon de commande' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'justificatif', label: 'Justificatif' },
    { value: 'contrat', label: 'Contrat' },
    { value: 'autre', label: 'Autre' }
];

// @ts-nocheck
export default function ExpenseForm({ isOpen, onClose, onSubmit, expense, users, projectCurrency }) {
    const [uploadedDocuments, setUploadedDocuments] = React.useState(expense?.documents || []);
    const [uploading, setUploading] = React.useState(false);
    const fileInputRef = React.useRef(null);

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors },
        reset
    } = useForm({
        defaultValues: expense || {
            category_key: '',
            sub_category_key: '',
            description: '',
            supplier: '',
            project_phase: '',
            expense_date: '',
            po_invoice_number: '',
            amount_ht: '',
            vat_rate: 20,
            amount_ttc: '',
            status: 'provisional',
            responsible_id: '',
            cost_center: '',
            payment_due_date: '',
            documents: []
        }
    });

    const selectedCategory = watch("category_key");
    const amountHT = watch("amount_ht");
    const vatRate = watch("vat_rate");

    // Calculate TTC automatically
    React.useEffect(() => {
        if (amountHT && vatRate) {
            const ttc = Number(amountHT) * (1 + Number(vatRate) / 100);
            setValue("amount_ttc", ttc.toFixed(2));
        }
    }, [amountHT, vatRate, setValue]);

    const availableSubCategories = React.useMemo(() => {
        const category = budgetStructure.find(c => c.key === selectedCategory);
        return category?.subCategories || [];
    }, [selectedCategory]);

    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(true);
        try {
            const uploadPromises = files.map(async (file) => {
                const { file_url } = await UploadFile({ file });
                return {
                    id: Math.random().toString(36).substr(2, 9), // Simple unique ID for client-side management
                    name: file.name,
                    type: 'autre', // Default type, user can change
                    file_url,
                    uploaded_date: new Date().toISOString()
                };
            });

            const newDocs = await Promise.all(uploadPromises);
            setUploadedDocuments(prev => [...prev, ...newDocs]);
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            alert('Erreur lors du téléchargement des fichiers');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveDocument = (docId) => {
        setUploadedDocuments(prev => prev.filter(doc => doc.id !== docId));
    };

    const handleDocumentTypeChange = (docId, newType) => {
        setUploadedDocuments(prev =>
            prev.map(doc =>
                doc.id === docId ? { ...doc, type: newType } : doc
            )
        );
    };

    const onSubmitForm = (data) => {
        onSubmit({
            ...data,
            amount_ht: Number(data.amount_ht),
            amount_ttc: Number(data.amount_ttc),
            vat_rate: Number(data.vat_rate),
            documents: uploadedDocuments
        });
        reset();
        setUploadedDocuments([]); // Clear documents after submission
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        {expense ? 'Modifier la dépense' : 'Nouvelle dépense'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
                    {/* Catégorisation */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="category_key">Catégorie principale *</Label>
                            <Controller
                                name="category_key"
                                control={control}
                                rules={{ required: "Catégorie requise" }}
                                render={({ field }) => (
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner une catégorie" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {budgetStructure.map(category => (
                                                <SelectItem key={category.key} value={category.key}>
                                                    {category.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {errors.category_key && <p className="text-red-500 text-sm">{errors.category_key.message}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="sub_category_key">Sous-catégorie *</Label>
                            <Controller
                                name="sub_category_key"
                                control={control}
                                rules={{ required: "Sous-catégorie requise" }}
                                render={({ field }) => (
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner une sous-catégorie" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableSubCategories.map(subCat => (
                                                <SelectItem key={subCat.key} value={subCat.key}>
                                                    {subCat.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                            {errors.sub_category_key && <p className="text-red-500 text-sm">{errors.sub_category_key.message}</p>}
                        </div>
                    </div>

                    {/* Description et Informations */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="description">Description de la dépense *</Label>
                            <Textarea
                                id="description"
                                {...register("description", { required: "Description requise" })}
                                placeholder="Détaillez la nature de cette dépense..."
                                className="h-20"
                            />
                            {errors.description && <p className="text-red-500 text-sm">{errors.description.message}</p>}
                        </div>

                        <div className="space-y-4"> {/* This div was added to group supplier and project_phase */}
                            <div className="space-y-2">
                                <Label htmlFor="supplier">Fournisseur</Label>
                                <Input id="supplier" {...register("supplier")} placeholder="Nom du fournisseur" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="project_phase">Phase du projet</Label>
                                <Select onValueChange={(value) => setValue("project_phase", value)} value={watch("project_phase")}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Sélectionner une phase" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="initiation">Initialisation</SelectItem>
                                        <SelectItem value="planning">Planification</SelectItem>
                                        <SelectItem value="execution">Exécution</SelectItem>
                                        <SelectItem value="testing">Tests</SelectItem>
                                        <SelectItem value="deployment">Déploiement</SelectItem>
                                        <SelectItem value="closure">Clôture</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {/* Informations financières */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="amount_ht">Montant HT ({projectCurrency}) *</Label>
                            <Input
                                id="amount_ht"
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                {...register("amount_ht", {
                                    required: "Montant HT requis",
                                    min: { value: 0, message: "Le montant doit être positif" }
                                })}
                            />
                            {errors.amount_ht && <p className="text-red-500 text-sm">{errors.amount_ht.message}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="vat_rate">Taux de TVA (%)</Label>
                            <Input
                                id="vat_rate"
                                type="number"
                                step="0.01"
                                placeholder="20"
                                {...register("vat_rate")}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="amount_ttc">Montant TTC ({projectCurrency})</Label>
                            <Input
                                id="amount_ttc"
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                {...register("amount_ttc")}
                                readOnly
                                className="bg-gray-50"
                            />
                        </div>
                    </div>

                    {/* Dates et références */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="expense_date">Date de la dépense</Label>
                            <Controller
                                name="expense_date"
                                control={control}
                                render={({ field }) => (
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start text-left">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {field.value ? format(new Date(field.value), 'dd/MM/yyyy', { locale: fr }) : "Sélectionner une date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={field.value ? new Date(field.value) : undefined}
                                                onSelect={(date) => field.onChange(date?.toISOString().split('T')[0])}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                )}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="po_invoice_number">N° BC / Facture</Label>
                            <Input
                                id="po_invoice_number"
                                placeholder="REF-2025-001"
                                {...register("po_invoice_number")}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="payment_due_date">Échéance de paiement</Label>
                            <Controller
                                name="payment_due_date"
                                control={control}
                                render={({ field }) => (
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start text-left">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {field.value ? format(new Date(field.value), 'dd/MM/yyyy', { locale: fr }) : "Échéance"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={field.value ? new Date(field.value) : undefined}
                                                onSelect={(date) => field.onChange(date?.toISOString().split('T')[0])}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                )}
                            />
                        </div>
                    </div>

                    {/* Documents Section */}
                    <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-center justify-between">
                            <Label className="text-base font-semibold">Documents justificatifs</Label>
                            <div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="gap-2"
                                >
                                    <Upload className="w-4 h-4" />
                                    {uploading ? 'Téléchargement...' : 'Ajouter fichiers'}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                />
                            </div>
                        </div>

                        {uploadedDocuments.length > 0 && (
                            <div className="space-y-3">
                                {uploadedDocuments.map((doc) => (
                                    <div key={doc.id} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                                        <FileText className="w-5 h-5 text-gray-500" />
                                        <div className="flex-1">
                                            <p className="font-medium text-sm">{doc.name}</p>
                                            <p className="text-xs text-gray-500">
                                                {doc.uploaded_date ? format(new Date(doc.uploaded_date), 'dd/MM/yyyy', { locale: fr }) : ''}
                                            </p>
                                        </div>
                                        <Select
                                            value={doc.type}
                                            onValueChange={(value) => handleDocumentTypeChange(doc.id, value)}
                                        >
                                            <SelectTrigger className="w-40">
                                                <SelectValue placeholder="Type de document" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {documentTypes.map(type => (
                                                    <SelectItem key={type.value} value={type.value}>
                                                        {type.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {doc.file_url && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                asChild
                                            >
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                                    <FileText className="w-4 h-4 text-blue-500 hover:text-blue-700" />
                                                </a>
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleRemoveDocument(doc.id)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className="text-xs text-gray-500">
                            Formats acceptés: PDF, DOC, DOCX, JPG, PNG, XLS, XLSX (Max 25MB par fichier)
                        </p>
                    </div>

                    {/* Statut et responsabilités */}
                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="status">Statut *</Label>
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Statut de la dépense" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(expenseStatusOptions).map(([value, config]) => (
                                                <SelectItem key={value} value={value}>
                                                    {config.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="responsible_id">Responsable</Label>
                            <Controller
                                name="responsible_id"
                                control={control}
                                render={({ field }) => (
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner un responsable" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {users.map(user => (
                                                <SelectItem key={user.id} value={user.id}>
                                                    {user.full_name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="cost_center">Centre de coût</Label>
                            <Input
                                id="cost_center"
                                placeholder="CC-XXX"
                                {...register("cost_center")}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Annuler
                        </Button>
                        <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                            {expense ? 'Modifier' : 'Créer'} la dépense
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
